import { formatBranchHash } from "@/components/table/branchHash";
import { registryDatasetDisplayLabel, type CheckpointTag } from "@/modules/training/public";
import { checkpointDisplay, evaluatedCheckpointFromParams } from "../evalRunPolicyModel";
import { resolveRunExecution, scenarioParams } from "../evalRunScenarioModel";
import type { CompareRun } from "./evalCompareModel";

export type CompareDiffRow = {
  key: string;
  values: (string | null)[];
  differs: boolean;
  display?: (string | null)[];
  titles?: (string | null)[];
};

/** 여러 열의 key-value 를 한 표의 행으로 합친다. 값이 다른 행이 위로 온다. */
export function entryRows(perRun: readonly Map<string, string>[]): CompareDiffRow[] {
  const keys: string[] = [];
  for (const entries of perRun) {
    for (const key of entries.keys()) if (!keys.includes(key)) keys.push(key);
  }
  const rows = keys.map((key) => {
    const values = perRun.map((entries) => entries.get(key) ?? null);
    return { key, values, differs: new Set(values).size > 1 };
  });
  return [...rows.filter((row) => row.differs), ...rows.filter((row) => !row.differs)];
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

export const SCENARIO_ROW_SEPARATOR = " / ";

export function compareScenarioRows(items: readonly CompareRun[]): CompareDiffRow[] {
  return entryRows(
    items.map((item) => {
      const entries = new Map<string, string>();
      const scenario = item.run.scenario;
      const execution = resolveRunExecution(item.run.summaryRow, item.run.detail, scenario);
      if (execution.tag !== null) entries.set("Docker tag", execution.tag);
      if (execution.image !== null) entries.set("Image", execution.image);
      for (const [key, value] of Object.entries(scenarioParams(scenario) ?? {})) {
        if (key !== "TASK")
          entries.set(`PARAMETERS${SCENARIO_ROW_SEPARATOR}${key}`, displayValue(value));
      }
      if (execution.task !== null)
        entries.set(`PARAMETERS${SCENARIO_ROW_SEPARATOR}TASK`, execution.task);
      return entries;
    }),
  );
}

export function comparePolicyRows(items: readonly CompareRun[]): CompareDiffRow[] {
  const fields: { key: string; get: (item: CompareRun) => string | null }[] = [
    {
      key: "code",
      get: ({ run: { summaryRow: row } }) => {
        return row.branch || row.git_short_hash
          ? formatBranchHash(row.branch, row.git_short_hash)
          : null;
      },
    },
    {
      // policy 를 학습한 config 다. 열 머리와 같은 값을 읽는다. baseline 열이 default 로 대체되면
      // 이 행이 다르게 강조된다.
      key: "config",
      get: (item) => item.config ?? null,
    },
    {
      key: "dataset",
      // Training module 공개 계약이 표시 정책을 소유한다. 여기서 직접 조합하면
      // 표마다 다른 이름이 나온다.
      get: ({ run: { summaryRow: row } }) =>
        row.dataset_registry_ref || row.upstream_dataset_label
          ? registryDatasetDisplayLabel(row.dataset_registry_ref, row.upstream_dataset_label)
          : null,
    },
  ];
  const rows = fields.map((field) => {
    const values = items.map((item) => field.get(item));
    return {
      key: field.key,
      values,
      differs: new Set(values.map((value) => value ?? "__missing__")).size > 1,
    };
  });
  return [...rows, checkpointRow(items)];
}

function checkpointRow(items: readonly CompareRun[]): CompareDiffRow {
  const tags = items.map((item) => evaluatedCheckpoint(item));
  const values = tags.map((tag) => (tag !== null ? `${tag.unit} ${tag.value}` : null));
  return {
    key: "checkpoint",
    values,
    differs: new Set(values.map((value) => value ?? "__missing__")).size > 1,
    display: tags.map((tag) => (tag !== null ? checkpointDisplay(tag).text : null)),
    titles: tags.map((tag) => (tag !== null ? checkpointDisplay(tag).title : null)),
  };
}

function evaluatedCheckpoint(item: CompareRun): CheckpointTag | null {
  const fromParams = evaluatedCheckpointFromParams(item.run.params);
  if (fromParams !== null) return fromParams;
  const summary = item.run.summaryRow;
  if (summary.epoch === null) return null;
  return { unit: summary.budget_unit, value: summary.epoch };
}

export function hasConfigurationDifferences(items: readonly CompareRun[]): boolean {
  const policyNames = items.map((item) => item.run.policyName);
  const policyDiffers =
    new Set(policyNames).size > 1 || comparePolicyRows(items).some((row) => row.differs);
  const scenarioDiffers = compareScenarioRows(items).some(
    (row) => row.differs && !isSimulationSeedRow(row.key),
  );
  return policyDiffers || scenarioDiffers;
}

function isSimulationSeedRow(key: string): boolean {
  const separator = key.indexOf(SCENARIO_ROW_SEPARATOR);
  if (separator < 0) return false;
  const parameter = key.slice(separator + SCENARIO_ROW_SEPARATOR.length);
  return parameter.trim().toUpperCase() === "SIMULATION_SEED";
}
